/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples: copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  acceptReportInvite,
  adminActivityActions,
  adminActivityAnalytics,
  adminDeleteUser,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListUsers,
  adminUpdateUser,
  analyzePrefill,
  type AssessmentCreateRequest,
  type AssessmentRecord,
  calibrateAssessment,
  type CalibrationRequest,
  changePassword,
  createAssessment,
  createReportInvite,
  deleteAccount,
  deleteAssessment,
  getActivity,
  getAssessment,
  getAssessmentDefinition,
  getAssessmentInternal,
  getAuthProviders,
  getHealth,
  getMe,
  getMeDetail,
  getReportInvite,
  getSharedAssessment,
  listAssessments,
  listMyAssessments,
  listReportInvites,
  login,
  logout,
  oauthSession,
  revokeReportInvite,
  setAssessmentShare,
  signup,
  updateProfile,
} from "@/lib/api/generated";
import type { Role } from "@/lib/roles";

// ─── Public queries / mutations ──────────────────────────────────────────

export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: async () => {
      const { data } = await getHealth();
      return data;
    },
  });
}

/**
 * The OAuth providers Supabase currently has enabled, so the sign-in surface
 * renders only buttons that actually work. Public and unauthenticated.
 */
export function useAuthProviders() {
  return useQuery({
    queryKey: ["auth", "providers"],
    queryFn: async () => {
      const { data } = await getAuthProviders();
      return data;
    },
    // The endpoint sets `Cache-Control: max-age=300`; mirror that TTL client-side.
    staleTime: 5 * 60_000,
    // Fail closed on the 502 (Supabase settings unreachable) rather than
    // hammering it: OAuthButtons renders nothing while this is errored, so the
    // email form still works: a briefly-missing button beats a dead one.
    retry: false,
  });
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const { data } = await getMe();
      return data;
    },
    // Don't retry on a 401: ProtectedRoute will redirect to /login.
    retry: false,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string; password: string; rememberMe?: boolean }) => {
      const { data } = await login({
        body: {
          email: vars.email,
          password: vars.password,
          remember_me: vars.rememberMe ?? false,
        },
      });
      return data;
    },
    onSuccess: () => {
      // A login can follow a session that ended WITHOUT the in-app logout
      // (expired/cleared cookie), so the cache may still hold the previous
      // identity's data, and a different user logging in on the same tab
      // would briefly see it. Clear everything, mirroring useLogout; the new
      // session refetches what it needs (including "me").
      queryClient.clear();
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useOauthSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { accessToken: string; rememberMe?: boolean }) => {
      const { data } = await oauthSession({
        body: {
          access_token: vars.accessToken,
          // OAuth sign-ins carry no "remember me" checkbox; social logins are
          // conventionally persistent, so default to a long-lived session.
          remember_me: vars.rememberMe ?? true,
        },
      });
      return data;
    },
    onSuccess: () => {
      // Same rationale as useLogin: a fresh identity must not inherit the
      // previous session's cached per-user data.
      queryClient.clear();
    },
  });
}

export function useSignup() {
  return useMutation({
    mutationFn: async (vars: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
    }) => {
      const { data } = await signup({
        body: {
          email: vars.email,
          password: vars.password,
          first_name: vars.firstName,
          last_name: vars.lastName,
        },
      });
      return data;
    },
  });
}

// ─── Profile ─────────────────────────────────────────────────────────────

export function useMeDetail() {
  return useQuery({
    queryKey: ["me", "detail"],
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data;
    },
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: ["activity"],
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string; lastName: string }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (vars: { currentPassword: string; newPassword: string }) => {
      await changePassword({
        body: { current_password: vars.currentPassword, new_password: vars.newPassword },
      });
    },
  });
}

/**
 * Self-service account deletion. The backend erases the account, its
 * credentials, and every assessment the user owns, then clears the session
 * cookie server-side. `password` confirms the action for email-identity
 * accounts. On success we wipe the entire cache: the session is over, so no
 * per-user data should survive it (mirrors useLogout).
 */
export function useDeleteAccount() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { password?: string | null }) => {
      await deleteAccount({ body: { password: vars.password ?? null } });
    },
    onSuccess: () => {
      queryClient.clear();
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => {
      const { data } = await adminListUsers();
      return data ?? [];
    },
  });
}

export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ["admin", "users", userId],
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data;
    },
    enabled: !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName?: string;
      lastName?: string;
      role?: Role;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: {
          first_name: vars.firstName,
          last_name: vars.lastName,
          role: vars.role,
        },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "users", vars.userId] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

// ─── Admin activity ──────────────────────────────────────────────────────

export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: ["admin", "activity", params],
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data;
    },
    // Keep the previous page on screen while fetching the next so the table
    // doesn't flash empty between paginations / filter changes.
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: ["admin", "activity", "analytics", days],
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: ["admin", "activity", "actions"],
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Assessments ─────────────────────────────────────────────────────────

export function useAssessmentDefinition() {
  return useQuery({
    queryKey: ["assessment", "definition"],
    queryFn: async () => {
      const { data } = await getAssessmentDefinition();
      return data;
    },
    // The question bank is static for the lifetime of the app.
    staleTime: Infinity,
  });
}

export function useAssessment(assessmentId: string | null) {
  return useQuery({
    queryKey: ["assessments", assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        return null;
      }
      const { data } = await getAssessment({ path: { assessment_id: assessmentId } });
      return data;
    },
    enabled: !!assessmentId,
    // The report page renders dedicated cards for 401/403/404, so retrying
    // those just delays the gate behind extra requests. Only retry transient
    // failures (default cap of 2). Errors are the ApiError `{ status }` shape.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 401 || status === 403 || status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Anonymous, token-gated read of a publicly shared report. The token is the
 * sole credential, so it is never cached under the owner's `assessmentId` key.
 */
export function useSharedAssessment(token: string | null) {
  return useQuery({
    queryKey: ["assessments", "shared", token],
    queryFn: async () => {
      if (!token) {
        return null;
      }
      const { data } = await getSharedAssessment({ path: { share_token: token } });
      return data;
    },
    enabled: !!token,
    // The backend returns the same 404 for an unknown, revoked, and ownerless
    // token: all of them mean "this link is dead", so retrying only delays the
    // dedicated state. Errors are the ApiError `{ status }` shape.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useAssessmentInternal(assessmentId: string | null) {
  return useQuery({
    queryKey: ["assessments", assessmentId, "internal"],
    queryFn: async () => {
      if (!assessmentId) {
        return null;
      }
      const { data } = await getAssessmentInternal({ path: { assessment_id: assessmentId } });
      return data;
    },
    enabled: !!assessmentId,
    // The expert detail page renders dedicated states for 401/403/404, so retrying
    // those just delays the gate behind extra requests. Only retry transient
    // failures (default cap of 2). Errors are the ApiError `{ status }` shape.
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 401 || status === 403 || status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export function useAssessments() {
  return useQuery({
    queryKey: ["assessments"],
    queryFn: async () => {
      const { data } = await listAssessments();
      return data ?? [];
    },
  });
}

export function useMyAssessments() {
  return useQuery({
    queryKey: ["assessments", "mine"],
    queryFn: async () => {
      const { data } = await listMyAssessments();
      return data ?? [];
    },
  });
}

export function useCreateAssessment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: AssessmentCreateRequest) => {
      const { data } = await createAssessment({ body: vars });
      return data;
    },
    onSuccess: () => {
      // A new assessment appears in the expert queue and the creator's
      // history: the ["assessments"] prefix invalidates both (incl. "mine").
      void queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
}

/**
 * Owner-only permanent deletion of one assessment (and its report, calibration,
 * share link, and invites, the backend cascades). Keyed by id like the other
 * per-report mutations. The ["assessments"] prefix invalidation covers the
 * owner's history ("mine"), the expert queue, and the per-id cache the report
 * page reads: the last one 404s after this, so consumers navigate away on
 * success rather than waiting for the refetch.
 */
export function useDeleteAssessment(assessmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await deleteAssessment({ path: { assessment_id: assessmentId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
}

export function useCalibrateAssessment(assessmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CalibrationRequest) => {
      const { data } = await calibrateAssessment({
        path: { assessment_id: assessmentId },
        body: vars,
      });
      return data;
    },
    onSuccess: () => {
      // Calibration changes the record's effective scores and its queue status.
      void queryClient.invalidateQueries({ queryKey: ["assessments"] });
      void queryClient.invalidateQueries({ queryKey: ["assessments", assessmentId] });
      // The expert detail view reads the calibrated internal record.
      void queryClient.invalidateQueries({ queryKey: ["assessments", assessmentId, "internal"] });
    },
  });
}

/**
 * Owner-only toggle of a report's public share link. The endpoint is
 * idempotent: enabling an already-shared report returns its existing token,
 * disabling revokes it for good (a later re-enable mints a fresh one).
 */
export function useSetShare(assessmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data } = await setAssessmentShare({
        path: { assessment_id: assessmentId },
        body: { enabled },
      });
      return data;
    },
    onSuccess: (data) => {
      // The response already carries the resulting ShareState, so write it
      // straight into the cached report: the toggle flips without a refetch.
      queryClient.setQueryData(
        ["assessments", assessmentId],
        (old: AssessmentRecord | null | undefined) => (old ? { ...old, share: data } : old),
      );
    },
  });
}

// ─── Report invites ──────────────────────────────────────────────────────

/**
 * Owner-only list of the invites issued for one report, newest first. The
 * backend NULLs `invite_token` on every accepted or revoked row, so a copyable
 * link exists only while an invite is still pending.
 */
export function useReportInvites(assessmentId: string | null) {
  return useQuery({
    queryKey: ["assessments", assessmentId, "invites"],
    queryFn: async () => {
      if (!assessmentId) {
        return [];
      }
      const { data } = await listReportInvites({ path: { assessment_id: assessmentId } });
      return data ?? [];
    },
    enabled: !!assessmentId,
  });
}

/**
 * Invite one colleague by email. Idempotent on the backend: re-inviting an
 * address that already holds a live invite returns that same invite rather than
 * rotating its token, so a link already sent keeps working.
 */
export function useCreateReportInvite(assessmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await createReportInvite({
        path: { assessment_id: assessmentId },
        body: { email: vars.email },
      });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["assessments", assessmentId, "invites"] });
    },
  });
}

/** Revoke one invite. Kills an unopened link and any read it already granted. */
export function useRevokeReportInvite(assessmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { inviteId: string }) => {
      await revokeReportInvite({
        path: { assessment_id: assessmentId, invite_id: vars.inviteId },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["assessments", assessmentId, "invites"] });
    },
  });
}

/**
 * Invitee-facing preview of an invite: report metadata only, never its
 * content. An account is required: the session email is the credential the
 * invite is bound to. The accept page renders a dedicated card for each of
 * 401 (signed out), 403 (signed in as someone else), and 404 (dead link), so
 * retrying any of them would only delay the gate.
 */
export function useReportInvite(token: string | null) {
  return useQuery({
    queryKey: ["report-invites", token],
    queryFn: async () => {
      if (!token) {
        return null;
      }
      const { data } = await getReportInvite({ path: { token } });
      return data;
    },
    enabled: !!token,
    retry: (failureCount, error) => {
      const status = (error as { status?: number } | undefined)?.status;
      if (status === 401 || status === 403 || status === 404) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

/**
 * Redeem an invite. Single-use: the backend NULLs the token on success, so the
 * preview cached under it is now dead, and the invitee has gained a report.
 */
export function useAcceptReportInvite(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await acceptReportInvite({ path: { token } });
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["report-invites", token] });
      void queryClient.invalidateQueries({ queryKey: ["assessments"] });
    },
  });
}

export function usePrefill() {
  return useMutation({
    mutationFn: async (vars: { files: File[]; pastedText?: string; url?: string }) => {
      // Multipart endpoint: the generated SDK serializes this body as
      // FormData (repeated `files` entries) via formDataBodySerializer.
      const { data } = await analyzePrefill({
        body: {
          files: vars.files,
          pasted_text: vars.pastedText,
          url: vars.url,
        },
      });
      return data;
    },
  });
}
